import { useState, type FormEvent } from 'react';
import { useCreatePurchaseOrder } from '../../../../hooks/usePurchaseOrders';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import type { PurchaseOrderDetail } from '../../../../services/purchaseOrders';
import { checkPoCreateOutcome } from '../poOutcome';
import {
    CREATE_PO_STEPS,
    emptyPurchaseOrderDraft,
    isStepComplete,
    type CreatePurchaseOrderDraft
} from './createPurchaseOrderDraft';

export interface UseCreatePurchaseOrderWizardArgs {
    initialPracticeId?: string | null;
    onCreated: (created: PurchaseOrderDetail) => void;
}

export function useCreatePurchaseOrderWizard({
    initialPracticeId,
    onCreated
}: UseCreatePurchaseOrderWizardArgs) {
    const { showToast } = useToast();
    const { mutateAsync: create, isPending } = useCreatePurchaseOrder();
    const [draft, setDraft] = useState<CreatePurchaseOrderDraft>(() =>
        emptyPurchaseOrderDraft(initialPracticeId)
    );
    const [stepIndex, setStepIndex] = useState(0);

    const step = CREATE_PO_STEPS[stepIndex];
    const isLastStep = stepIndex === CREATE_PO_STEPS.length - 1;
    // Cascades like CreateChargeModal's maxReachable: a chip is only
    // reachable once every step before it is valid.
    const detailsValid = isStepComplete(draft, 'details');
    const linesValid = isStepComplete(draft, 'lines');
    const maxReachable = !detailsValid ? 0 : !linesValid ? 1 : 2;

    const update = (changes: Partial<CreatePurchaseOrderDraft>) =>
        setDraft((prev) => ({ ...prev, ...changes }));

    // A new practice invalidates the charge and everything after it; a new
    // charge invalidates only the line-item selection.
    const setPractice = (practiceId: string | null) =>
        setDraft((prev) => ({ ...emptyPurchaseOrderDraft(practiceId), sentAt: prev.sentAt }));
    const setCharge = (chargeId: string | null) => update({ chargeId, chargeLineItemIds: [] });

    const goNext = () => {
        if (stepIndex === 0 && !draft.practiceId) {
            showToast('Please select a practice', 'error');
            return;
        }
        if (stepIndex === 0 && !draft.chargeId) {
            showToast('Please select a charge', 'error');
            return;
        }
        if (stepIndex === 1 && draft.chargeLineItemIds.length === 0) {
            showToast('Select at least one line item', 'error');
            return;
        }
        setStepIndex((i) => Math.min(CREATE_PO_STEPS.length - 1, i + 1));
    };

    // Implicit form submission (Enter in any input) fires on every step -
    // before the last step it must ADVANCE, not record the order.
    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!isLastStep) {
            goNext();
            return;
        }
        if (!draft.vendorId || !draft.orderMethodId) {
            showToast('Select a vendor and ordering method', 'error');
            return;
        }
        const poNumber = draft.poNumber.trim() || undefined;
        try {
            const created = await create({
                chargeLineItemIds: draft.chargeLineItemIds,
                vendorId: draft.vendorId,
                orderMethodId: draft.orderMethodId,
                poNumber,
                sentAt: new Date(draft.sentAt).toISOString()
            });
            const outcome = checkPoCreateOutcome(created, {
                chargeLineItemIds: draft.chargeLineItemIds,
                vendorId: draft.vendorId,
                poNumber
            });
            showToast(outcome.message, outcome.held ? 'success' : 'error');
            if (outcome.held) onCreated(created);
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to record the purchase order'), 'error');
        }
    };

    return {
        draft,
        update,
        setPractice,
        setCharge,
        step,
        stepIndex,
        maxReachable,
        isLastStep,
        goNext,
        goToStep: setStepIndex,
        handleSubmit,
        isSubmitting: isPending
    };
}
